import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, IconButton } from '@mui/material';
import { Dashboard as DashboardIcon, People as ClientsIcon } from '@mui/icons-material';
import { DashboardLayout } from '@components/layout/DashboardLayout.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { PmProfileSidebar, PmProfileTab } from '@components/pm/PmProfileSidebar.tsx';
import { PmExpertDashboardTab } from '@components/pm/expert/PmExpertDashboardTab.tsx';
import { PmExpertClientsTab } from '@components/pm/expert/PmExpertClientsTab.tsx';
import { pmUsersService } from '@services/pm-users.service.ts';
import type { AdminExpertDetail } from '@services/admin-users.service.ts';

// Картка експерта для продакт-менеджера (2026-10-10): /pm/expert/:id/dashboard|clients.
// Той самий каркас, що й картка клієнта продакта (PmClientPage): правий сайдбар з меню, розділи поки заготовки.
// Продакт бачить будь-якого експерта (бек: GET pm/experts/:id).
const TABS: PmProfileTab[] = [
  { path: 'dashboard', label: 'Дашборд', icon: <DashboardIcon /> },
  { path: 'clients', label: 'Клієнти', icon: <ClientsIcon /> },
];

export const PmExpertPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [expert, setExpert] = useState<AdminExpertDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const activeTab = TABS.find((tab) => location.pathname.includes(`/pm/expert/${id}/${tab.path}`)) ?? TABS[0];
  const title = `Експерт → ${activeTab.label}`;

  useEffect(() => {
    const expertId = Number(id);
    if (!expertId) {
      setError('Експерта не знайдено.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    pmUsersService
      .getExpert(expertId)
      .then(setExpert)
      .catch((err) => {
        const message = err.response?.data?.message;
        setError(Array.isArray(message) ? message.join(', ') : message || 'Не вдалося завантажити інформацію про експерта.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading || error || !expert) {
    return (
      <DashboardLayout titleOverride={title}>
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          {loading ? <CircularProgress /> : <Alert severity={error ? 'error' : 'info'}>{error ?? 'Експерта не знайдено.'}</Alert>}
        </Box>
      </DashboardLayout>
    );
  }

  const name = `${expert.name || ''} ${expert.surname || ''}`.trim();

  const rightAction = (
    <IconButton onClick={() => setMobileSidebarOpen(true)} color="inherit" sx={{ p: 0 }}>
      <UserAvatar path={expert.avatar} name={expert.name} sx={{ width: 32, height: 32 }} />
    </IconButton>
  );

  return (
    <DashboardLayout rightAction={rightAction} titleOverride={title} disableContentPadding>
      <Box
        sx={{
          pt: 1,
          px: 1.5,
          pb: 1,
          mr: { xs: 0, md: '200px' },
          height: 'calc(100vh - 52px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<PmExpertDashboardTab />} />
            <Route path="clients" element={<PmExpertClientsTab />} />
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </Box>
        <PmProfileSidebar
          kind="expert"
          tabs={TABS}
          activeTab={activeTab.path}
          onTabChange={(path) => navigate(`/pm/expert/${id}/${path}`)}
          name={name}
          avatar={expert.avatar}
          isMobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />
      </Box>
    </DashboardLayout>
  );
};
